import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { usePlatformAdmin } from "../../../hooks/use-platform-admin";
import { useByocEnabled } from "../../../hooks/use-byoc-enabled";
import { usePmHandover } from "../../../hooks/use-pm-handover";
import { usePageBar } from "../../../components/layout/page-bar";
import { useCheckoutSuccessListener } from "../../../hooks/useCheckoutSuccessListener";
import { useCurrencyContext } from "../../../hooks/useCurrencyContext";
import { localizeUsdCents } from "../../../lib/currency";
import { billingApi, PricingResponse, SubscriptionResponse } from "../../../lib/api/billing";
import { creditsApi, CreditDetailsResponse, ModelUsageItem, ProjectUsageItem } from "../../../lib/api/credits";
import { analytics } from "@lib/analytics";
import { PlanType, PricingPlan } from "@optidev/shared-types";
import { PricingCard } from "./PricingCard";
import { PmHandoverBanner } from "./pm-handover-banner";
import { PLAN_INFO, CURRENCY_SYMBOLS, getPlanPrices } from "./plan-info";
import { CurrentPlanCardWithCredits } from "./CurrentPlanCardWithCredits";
import { ModelUsageCard } from "./ModelUsageCard";
import { ProjectUsageCard } from "./ProjectUsageCard";

// Hide usage breakdown in production — kept for local/sandbox only
// Usage by Model / Usage by Project visibility:
//  - sandbox envs: visible to every authenticated user (for dogfooding/debug)
//  - production:   visible to platform admins only (DB-granted secretToken)
// Computed inside the component (see `canViewUsageBreakdown` below) so the
// `usePlatformAdmin` hook can be consulted at render time.
const IS_PRODUCTION = import.meta.env.VITE_ENV === "production";
import { TopUpCreditsModal } from "./TopUpCreditsModal";
import { UpgradeConfirmModal } from "./UpgradeConfirmModal";
import { EmbedBillingView } from "./EmbedBillingView";
import { useEmbedStore } from "../../../stores/embed-store";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Loader2, AlertCircle, Globe, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button";
import { CurrencySelect } from "./CurrencySelect";

export const BillingPage: React.FC = () => {
  const { getIdToken, user } = useAuth();
  const { isAdminOrOwner, isOwner } = usePermissions();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { enabled: byocEnabled } = useByocEnabled();
  // In production we restrict the Usage by Model / Usage by Project breakdown
  // to platform admins only (DB-granted secretToken — currently a small list
  // of OptiSigns staff). In sandbox envs every authenticated user can view it
  // to make dogfooding easier.
  const canViewUsageBreakdown = !IS_PRODUCTION || isPlatformAdmin;
  const { isEmbedded, embedSource } = useEmbedStore();
  const { context: currencyContext } = useCurrencyContext();
  // Payment-method handover state shared with the app-wide shell bar
  const {
    pending: pendingPmHandover,
    refresh: refreshPmHandover,
    resolveLocally: resolvePmHandoverLocally,
  } = usePmHandover();

  // Shell page bar (registered after Settings' own entry, so this wins while mounted)
  usePageBar({
    title: "Billing",
    description: "Manage your plan, credits, and payment method.",
  });
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditDetails, setCreditDetails] = useState<CreditDetailsResponse | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("usd");

  // Drive the displayed/selected currency from the resolved currency context
  // (pinned subscription currency, else workspace preference, else USD).
  useEffect(() => {
    setSelectedCurrency(currencyContext.displayCurrency);
  }, [currencyContext.displayCurrency]);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [modelUsage, setModelUsage] = useState<ModelUsageItem[] | null>(null);
  const [modelUsageLoading, setModelUsageLoading] = useState(canViewUsageBreakdown);
  const [projectUsage, setProjectUsage] = useState<ProjectUsageItem[] | null>(null);
  const [projectUsageLoading, setProjectUsageLoading] = useState(canViewUsageBreakdown);
  const [hasCardOnFile, setHasCardOnFile] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    planType: PlanType.STANDARD | PlanType.PRO;
    interval: 'month' | 'year';
  }>({ open: false, planType: PlanType.STANDARD, interval: 'month' });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadSubscription();
    loadCreditDetails();
    loadPricing();
    loadPaymentMethods();
  }, []);

  // Fetch the usage breakdown only after we know the viewer is allowed to see
  // it. In production this waits for `usePlatformAdmin` to resolve so we don't
  // 403 the user with an immediate request. In sandbox `canViewUsageBreakdown`
  // is true on first render so this fires right away.
  useEffect(() => {
    if (!canViewUsageBreakdown) return;
    loadModelUsage();
    loadProjectUsage();
  }, [canViewUsageBreakdown]);

  // Detect Stripe checkout return → fire first_subscription GTM event and clean URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    // localStorage dedup — prevents double-fire on hard refresh before URL is cleaned
    const uid = user?.id;
    const dedupKey = uid ? `sub:${uid}:first_subscription_fired` : null;
    if (dedupKey && localStorage.getItem(dedupKey)) {
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (dedupKey) localStorage.setItem(dedupKey, 'true');
    analytics.track('first_subscription');
    searchParams.delete('session_id');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, user?.id]);

  // Refresh credits when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadCreditDetails();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Embed top-up: Stripe success tab broadcasts to this page, refresh balance + toast.
  useCheckoutSuccessListener('top-up', () => {
    loadCreditDetails();
    setTopUpModalOpen(false);
    toast.success('Payment successful. Credits updated.');
  });

  // Auto-toggle to annual if user's current subscription is annual
  useEffect(() => {
    if (subscription?.interval === "year") {
      setIsAnnual(true);
    }
  }, [subscription?.interval]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      const data = await billingApi.getSubscription(token);
      setSubscription(data);
    } catch (err: any) {
      console.error("Error loading subscription:", err);
      setError(err.response?.data?.message || "Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  const loadCreditDetails = async () => {
    try {
      setCreditsLoading(true);
      const token = await getIdToken();
      if (!token) {
        return;
      }
      const data = await creditsApi.getDetails(token);
      setCreditDetails(data);
    } catch (err: any) {
      console.error('Error loading credit details:', err);
      // Don't show error for credits - it's not critical
    } finally {
      setCreditsLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const data = await billingApi.getPricing();
      setPricing(data);
      // selectedCurrency is driven by the resolved currency context (see effect below),
      // not the public pricing default.
    } catch (err: any) {
      console.error('Error loading pricing:', err);
    }
  };

  const loadModelUsage = async () => {
    try {
      setModelUsageLoading(true);
      const token = await getIdToken();
      if (!token) return;
      const data = await creditsApi.getModelUsage(token);
      setModelUsage(data);
    } catch (err: any) {
      console.error('Error loading model usage:', err);
    } finally {
      setModelUsageLoading(false);
    }
  };

  const loadProjectUsage = async () => {
    try {
      setProjectUsageLoading(true);
      const token = await getIdToken();
      if (!token) return;
      const data = await creditsApi.getProjectUsage(token);
      setProjectUsage(data);
    } catch (err: any) {
      console.error('Error loading project usage:', err);
    } finally {
      setProjectUsageLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const pmResponse = await billingApi.getPaymentMethods(token);
      setHasCardOnFile(pmResponse.paymentMethods.length > 0);
    } catch {
      // Best-effort — doesn't block the page
    }
  };

  const handleUpgrade = async (planType: PlanType) => {
    if (planType === PlanType.ENTERPRISE) {
      window.open("https://calendly.com/optidev/demo/", "_blank", "noopener,noreferrer");
      return;
    }

    const interval = isAnnual ? "year" : "month";
    const hasActiveSubscription = subscription?.stripeSubscriptionId;

    if (hasActiveSubscription) {
      // Show confirmation modal for upgrades
      setUpgradeModal({
        open: true,
        planType: planType as PlanType.STANDARD | PlanType.PRO,
        interval,
      });
    } else {
      // New subscription - use Stripe Checkout
      try {
        setCheckoutLoading(planType);
        const token = await getIdToken();
        if (!token) {
          toast.error("Authentication required");
          return;
        }

        const { url } = await billingApi.createCheckoutSession(
          token,
          planType as PlanType.STANDARD | PlanType.PRO,
          interval,
          selectedCurrency
        );
        window.location.href = url;
      } catch (err: any) {
        console.error("Error creating checkout:", err);
        toast.error(
          err.response?.data?.message || "Failed to start checkout"
        );
        setCheckoutLoading(null);
      }
    }
  };

  const handleManageBilling = async () => {
    try {
      setPortalLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const { url } = await billingApi.createPortalSession(token);

      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err: any) {
      console.error("Error creating portal session:", err);
      toast.error(
        err.response?.data?.message || "Failed to open billing portal"
      );
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const currentPlanType = subscription?.planType || PlanType.FREE;

  // Embed mode: show simplified billing view
  if (isEmbedded && embedSource === 'optisigns') {
    return (
      <>
        <EmbedBillingView
          subscription={subscription}
          creditDetails={creditDetails}
          creditsLoading={creditsLoading}
          onTopUp={() => setTopUpModalOpen(true)}
        />
        <TopUpCreditsModal
          isOpen={topUpModalOpen}
          onClose={() => {
            setTopUpModalOpen(false);
            loadCreditDetails();
          }}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment-method handover banner (new owner after ownership transfer) */}
      {pendingPmHandover && isOwner && (
        <PmHandoverBanner
          onDismissed={() => {
            resolvePmHandoverLocally();
            void refreshPmHandover(true);
            loadSubscription();
          }}
        />
      )}

      {/* Plan status + billing-interval toggle (page title lives in the shell PageBar) */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pb-5 border-b border-border/40">
        <p className="text-sm text-muted-foreground">
          {currentPlanType === PlanType.FREE
            ? "Choose a plan to unlock more features"
            : `Currently on ${currentPlanType.charAt(0).toUpperCase() + currentPlanType.slice(1)} plan`}
        </p>
        <div className="flex items-center gap-3">
          {/* Billing toggle */}
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5">
            <Label
              htmlFor="annual-toggle"
              className={`text-xs cursor-pointer transition-all ${!isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Monthly
            </Label>
            <Switch
              id="annual-toggle"
              checked={isAnnual}
              onCheckedChange={(v) => {
                setIsAnnual(v);
                setUpgradeModal(prev => ({ ...prev, open: false }));
              }}
              className="data-[state=checked]:bg-primary scale-90"
            />
            <Label
              htmlFor="annual-toggle"
              className={`text-xs cursor-pointer transition-all ${isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Annual
            </Label>
            <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
              Save 10%
            </span>
          </div>

          {/* Currency */}
          <CurrencySelect />
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[PlanType.STANDARD, PlanType.PRO, PlanType.ENTERPRISE].map((planType) => {
          const info = PLAN_INFO[planType];
          const symbol = CURRENCY_SYMBOLS[selectedCurrency] || "$";

          const { monthlyPrice, yearlyPrice } = getPlanPrices(
            pricing,
            planType,
            selectedCurrency,
          );

          return (
            <PricingCard
              key={planType}
              name={info.name}
              description={info.description}
              monthlyPrice={monthlyPrice}
              yearlyPrice={yearlyPrice}
              features={info.features}
              featureHeader={info.featureHeader}
              monthlyCredits={info.monthlyCredits}
              isAnnual={isAnnual}
              ctaText={info.ctaText}
              onUpgrade={() => handleUpgrade(planType)}
              loading={checkoutLoading === planType}
              currentPlanType={currentPlanType}
              currentInterval={subscription?.interval || null}
              planType={planType}
              currencySymbol={symbol}
              disabled={!isAdminOrOwner}
            />
          );
        })}
      </div>

      {/* Current Plan & Credits */}
      {subscription && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Subscription Details
          </h3>
          <div className="space-y-3">
            <CurrentPlanCardWithCredits
              planType={currentPlanType}
              amount={localizeUsdCents(subscription.amount, currencyContext.displayCurrency, currencyContext.rates)}
              currency={currencyContext.displayCurrency}
              interval={subscription.interval}
              currentPeriodEnd={subscription.currentPeriodEnd}
              onManageBilling={handleManageBilling}
              onTopUp={() => setTopUpModalOpen(true)}
              hasCardOnFile={hasCardOnFile}
              loading={portalLoading}
              cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
              pendingPlan={subscription.pendingPlan ? {
                ...subscription.pendingPlan,
                amount: localizeUsdCents(subscription.pendingPlan.amount, currencyContext.displayCurrency, currencyContext.rates),
              } : subscription.pendingPlan}
              freeDaily={creditDetails?.freeDaily ?? null}
              creditBalance={creditDetails?.balance ?? null}
              subscriptionBalance={creditDetails?.subscriptionBalance ?? null}
              topupBalance={creditDetails?.topupBalance ?? null}
              creditAllocated={creditDetails?.allocated ?? null}
              creditUsed={creditDetails?.currentCycleUsed ?? null}
              creditNextReset={creditDetails?.nextResetAt ?? null}
              creditsLoading={creditsLoading}
            />

            {/* Top Up Credits - paid plans only */}
            {currentPlanType !== PlanType.FREE && (
              <div className="flex flex-col gap-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Need more credits?</p>
                    <p className="text-xs text-muted-foreground">Purchase additional credits for your workspace</p>
                  </div>
                  <Button onClick={() => setTopUpModalOpen(true)} disabled={!isAdminOrOwner} className="text-xs h-8 px-3 bg-foreground hover:bg-foreground/90 text-background">
                    Top up credits
                  </Button>
                </div>
                {byocEnabled && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t border-border/40 pt-3">
                    <Sparkles className="h-3 w-3 text-purple-500" />
                    <span>Have a Claude subscription?</span>
                    <Link to="/dashboard/settings/workspace?claude=reconnect" className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
                      Connect for unlimited usage
                    </Link>
                  </div>
                )}
              </div>
            )}

            {canViewUsageBreakdown && (
              <div className="space-y-1 border-t border-border/40 pt-4">
                <ModelUsageCard usage={modelUsage} loading={modelUsageLoading} />
                <ProjectUsageCard usage={projectUsage} loading={projectUsageLoading} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Up Credits Modal */}
      <TopUpCreditsModal
        isOpen={topUpModalOpen}
        onClose={() => {
          setTopUpModalOpen(false);
          loadCreditDetails();
        }}
      />

      {/* Upgrade Confirmation Modal */}
      <UpgradeConfirmModal
        isOpen={upgradeModal.open}
        onClose={() => {
          setUpgradeModal(prev => ({ ...prev, open: false }));
        }}
        onSuccess={async () => {
          await loadSubscription();
          await loadCreditDetails();
        }}
        planType={upgradeModal.planType}
        interval={upgradeModal.interval}
      />
    </div>
  );
};
